import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, Trash2, Mail, Phone, MessageSquare, Filter, Home } from "lucide-react";

export const AdminContacts = () => {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  useEffect(() => {
    fetchContacts();
  }, [token, filterStatus]);
  
  const fetchContacts = async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const response = await axios.get(`${API}/admin/contacts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des messages");
    } finally {
      setLoading(false);
    }
  };
  
  const updateStatus = async (contactId, newStatus) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Statut mis à jour");
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };
  
  const handleDelete = async (contactId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce message ?")) return;
    
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message supprimé");
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-800 border-blue-200",
      read: "bg-gray-100 text-gray-800 border-gray-200",
      replied: "bg-green-100 text-green-800 border-green-200"
    };
    const labels = {
      new: "Nouveau",
      read: "Lu",
      replied: "Répondu"
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-contacts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl text-[#0A0A0A]">Messages</h1>
            <p className="text-gray-500 mt-1">{contacts.length} message(s)</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500" />
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="new">Nouveau</SelectItem>
                <SelectItem value="read">Lu</SelectItem>
                <SelectItem value="replied">Répondu</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Contacts Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Sujet</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-500">
                    Aucun message reçu
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow 
                    key={contact.id} 
                    className={contact.status === 'new' ? 'bg-blue-50/50' : ''}
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-sm text-gray-500">{contact.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {contact.property_id && (
                          <Home size={14} className="text-[#D4AF37]" />
                        )}
                        <span className="truncate max-w-[200px]">{contact.subject}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600 truncate max-w-[200px]">
                        {contact.message}
                      </p>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(contact.created_at)}
                    </TableCell>
                    <TableCell>{getStatusBadge(contact.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedContact(contact);
                            setDialogOpen(true);
                            if (contact.status === 'new') {
                              updateStatus(contact.id, 'read');
                            }
                          }}
                          data-testid={`view-contact-${contact.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.location.href = `mailto:${contact.email}`}
                          data-testid={`email-contact-${contact.id}`}
                        >
                          <Mail size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(contact.id)}
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Contact Detail Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Détails du message</DialogTitle>
            </DialogHeader>
            
            {selectedContact && (
              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nom</p>
                    <p className="font-medium">{selectedContact.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-medium">{formatDate(selectedContact.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a href={`mailto:${selectedContact.email}`} className="font-medium text-[#D4AF37] hover:underline">
                      {selectedContact.email}
                    </a>
                  </div>
                  {selectedContact.phone && (
                    <div>
                      <p className="text-sm text-gray-500">Téléphone</p>
                      <a href={`tel:${selectedContact.phone}`} className="font-medium text-[#D4AF37] hover:underline">
                        {selectedContact.phone}
                      </a>
                    </div>
                  )}
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Sujet</p>
                  <p className="font-medium">{selectedContact.subject}</p>
                </div>
                
                {selectedContact.property_id && (
                  <div className="p-3 bg-[#D4AF37]/10 rounded flex items-center gap-2">
                    <Home size={18} className="text-[#D4AF37]" />
                    <span className="text-sm">Ce message concerne un bien immobilier</span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open(`/bien/${selectedContact.property_id}`, '_blank')}
                    >
                      Voir le bien
                    </Button>
                  </div>
                )}
                
                <div>
                  <p className="text-sm text-gray-500 mb-2">Message</p>
                  <div className="p-4 bg-gray-50 rounded whitespace-pre-line">
                    {selectedContact.message}
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Statut</p>
                    <Select 
                      value={selectedContact.status} 
                      onValueChange={(value) => {
                        updateStatus(selectedContact.id, value);
                        setSelectedContact({ ...selectedContact, status: value });
                      }}
                    >
                      <SelectTrigger className="w-40" data-testid="update-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Nouveau</SelectItem>
                        <SelectItem value="read">Lu</SelectItem>
                        <SelectItem value="replied">Répondu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex gap-2">
                    {selectedContact.phone && (
                      <Button variant="outline" onClick={() => window.location.href = `tel:${selectedContact.phone}`}>
                        <Phone size={16} className="mr-2" />
                        Appeler
                      </Button>
                    )}
                    <Button 
                      className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
                      onClick={() => window.location.href = `mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                    >
                      <Mail size={16} className="mr-2" />
                      Répondre par email
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
